import React, { useState, useEffect } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { apiService, DEFAULT_SUPPORTED_FILE_TYPES } from '../../../services/api';
import FileUploadZone from '../shared/FileUploadZone';
import { isAwaitingReviewOnly } from '../../../utils/datasetReviewState';
import { requireVerb } from '../../../utils/reviewRequiredNotice';
import { notifyRejectedUploads, rejectedUploadNotice } from '../../../utils/uploadOutcome';
import './EditDatasetModal.css';

/**
 * Side drawer modal for editing a dataset.
 * Section 1: Edit name & description.
 * Section 2: View existing files summary, upload new files, and trigger pipeline processing.
 */
const EditDatasetModal = ({ dataset, onClose, onSave, onShowNotification, onOpenDocumentReview }) => {
  const [name, setName] = useState(dataset?.name || '');
  const [description, setDescription] = useState(dataset?.description || '');
  const [saving, setSaving] = useState(false);

  // File upload state
  const [newFiles, setNewFiles] = useState([]);
  const [filesSummary, setFilesSummary] = useState(null);
  const [supportedFileTypes, setSupportedFileTypes] = useState(DEFAULT_SUPPORTED_FILE_TYPES);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const hasActiveJobs = dataset?.job_status &&
    ['processing', 'queued'].includes(dataset.job_status.toLowerCase());

  // PRD-1359: parked-document count, from the dataset record rather than the
  // files summary — `getDatasetFilesSummary` has no review bucket, and
  // `file_counts.awaiting_review` is the count every other surface reads.
  const reviewCount = isAwaitingReviewOnly(dataset)
    ? (dataset.file_counts?.awaiting_review ?? 0)
    : 0;

  // Load file summary and supported file types on mount
  useEffect(() => {
    let cancelled = false;

    apiService.getDatasetFilesSummary(dataset.id).then((data) => {
      if (!cancelled) setFilesSummary(data);
    }).catch(() => {});

    apiService.getSupportedFileTypes().then((data) => {
      if (!cancelled) setSupportedFileTypes(data);
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [dataset.id]);

  const handleSave = async () => {
    if (!name.trim()) {
      onShowNotification('Dataset name is required.', 'error');
      return;
    }

    setSaving(true);
    try {
      await onSave(dataset.id, { name: name.trim(), description: description.trim() });
      onShowNotification('Dataset updated successfully.', 'success');
      onClose();
    } catch (err) {
      onShowNotification('Failed to update dataset. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAndProcess = async () => {
    if (newFiles.length === 0) {
      onShowNotification('Please select files to upload.', 'error');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      // Step 1: Upload files
      const uploadResp = await apiService.uploadFiles(dataset.id, newFiles, 'local');

      // Step 2: Trigger pipeline (routing + extraction for uploaded files only)
      await apiService.extractFields(dataset.id);

      // PRD-1628: a 207 kept the good files. The drawer stays open listing the
      // rejected ones, so a valid copy can go up from here straight away.
      if (notifyRejectedUploads(uploadResp, onShowNotification)) {
        setUploadError(rejectedUploadNotice(uploadResp));
        setNewFiles([]);
        apiService.getDatasetFilesSummary(dataset.id).then(setFilesSummary).catch(() => {});
        return;
      }
      onShowNotification('Files uploaded. Pipeline processing has started.', 'success');
      onClose();
    } catch (err) {
      const message = err.message || 'Failed to upload and process files.';
      setUploadError(message);
      onShowNotification(message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const isProcessing = saving || uploading;

  return (
    <div className="edit-dataset-overlay" onClick={onClose}>
      <div className="edit-dataset-drawer" onClick={e => e.stopPropagation()}>
        <div className="edit-dataset-drawer-header">
          <h2>Edit Dataset</h2>
          <button className="edit-dataset-drawer-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="edit-dataset-drawer-body">
          {/* Section 1: Details */}
          <div className="edit-dataset-section">
            <h3 className="edit-dataset-section-title">Details</h3>
            <div className="edit-dataset-field">
              <label htmlFor="edit-dataset-name">Name</label>
              <input className="resui-input"
                id="edit-dataset-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter dataset name"
              />
            </div>

            <div className="edit-dataset-field">
              <label htmlFor="edit-dataset-description">Description</label>
              <textarea className="resui-textarea"
                id="edit-dataset-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter dataset description"
              />
            </div>

            <div className="edit-dataset-details-actions">
              <button className="edit-dataset-btn-save" onClick={handleSave} disabled={isProcessing}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>

          {/* Section 2: Files */}
          <div className="edit-dataset-section">
            <h3 className="edit-dataset-section-title">Files</h3>

            {/* Existing files summary */}
            {filesSummary && filesSummary.total > 0 && (
              <div className="edit-dataset-files-summary">
                <Icon name="folder" className="edit-dataset-files-summary-icon" size={20} />
                {/* Flex so the review group below can push itself to the right
                    edge with margin-left:auto, per Figma 1022-4429. */}
                <span className="edit-dataset-files-summary-body">
                  {filesSummary.total} existing file{filesSummary.total !== 1 ? 's' : ''}
                  {/* PRD-1359 (feedback doc §Edit dataset drawer): while documents
                      are parked, the complete chip is suppressed and replaced by the
                      review chip. Showing a green "N complete" beside a review
                      warning reads as "nothing to do here", which is the opposite of
                      the point. */}
                  {reviewCount > 0 ? (
                    // Figma 1022-4429 pushes the chip and its link to the right
                    // edge of the row, away from the file count on the left.
                    <span className="edit-dataset-files-review-group">
                      <Chip variant="alert" className="edit-dataset-files-chip">
                        {/* sentence case, and agreeing with the count */}
                        {reviewCount} {requireVerb(reviewCount)} review
                      </Chip>
                      {onOpenDocumentReview && (
                        <button
                          type="button"
                          className="resui-link"
                          onClick={() => onOpenDocumentReview(dataset)}
                        >
                          Review fields
                        </button>
                      )}
                    </span>
                  ) : (
                    filesSummary.complete > 0 && <Chip variant="success" className="edit-dataset-files-chip">{filesSummary.complete} complete</Chip>
                  )}
                  {filesSummary.processing > 0 && <Chip variant="processing" className="edit-dataset-files-chip">{filesSummary.processing} processing</Chip>}
                  {filesSummary.failed > 0 && <Chip variant="error" className="edit-dataset-files-chip">{filesSummary.failed} failed</Chip>}
                  {filesSummary.rejected > 0 && <Chip variant="error" className="edit-dataset-files-chip">{filesSummary.rejected} rejected</Chip>}
                  {/* No "N pending" chip: uploaded-but-unprocessed is not a state
                      the author acts on here — design's call after the review
                      walkthrough (2026-10-06). The count still shows in the total. */}
                </span>
              </div>
            )}

            {/* Active job warning */}
            {hasActiveJobs && (
              <div className="edit-dataset-warning">
                <Icon name="warning" />
                <span>A pipeline is currently running. Upload and processing are disabled until the current job completes.</span>
              </div>
            )}

            {/* Upload zone */}
            <FileUploadZone
              selectedFiles={newFiles}
              onFilesChanged={setNewFiles}
              supportedFileTypes={supportedFileTypes}
              compact
            />

            {uploadError && (
              <div className="edit-dataset-upload-error">{uploadError}</div>
            )}

            <div className="edit-dataset-files-actions">
              <button
                className="resui-btn resui-btn--primary"
                onClick={handleUploadAndProcess}
                disabled={isProcessing || newFiles.length === 0 || hasActiveJobs}
              >
                <span className="resui-btn-label">{uploading ? 'Uploading…' : 'Upload and Process'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="edit-dataset-drawer-footer">
          <button className="resui-btn resui-btn--secondary" onClick={onClose} disabled={isProcessing}>
            <span className="resui-btn-label">Cancel</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDatasetModal;
